import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getInvoiceTemplateById,
  createInvoiceTemplate,
  updateInvoiceTemplate,
  previewInvoiceNumber
} from '../services/invoiceTemplateService';
import { useTranslation } from 'react-i18next';

const InvoiceTemplateForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'outgoing',
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientNip: '',
    defaultDescription: '',
    currency: 'USD',
    taxRate: 0,
    paymentTermsDays: 30,
    notes: '',
    numberPrefix: 'INV',
    numberSuffix: '',
    numberFormat: '{PREFIX}-{YEAR}-{NUMBER}',
    numberPadding: 4,
    resetCounterAnnually: true,
    resetCounterMonthly: false,
    isDefault: false,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewNumber, setPreviewNumber] = useState('');

  useEffect(() => {
    if (isEditMode) {
      fetchTemplate();
    }
  }, [id]);

  useEffect(() => {
    if (!isEditMode) {
      updatePreview();
    }
  }, [formData.numberPrefix, formData.numberSuffix, formData.numberFormat, formData.numberPadding]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const data = await getInvoiceTemplateById(id);
      setFormData(data.template);
      if (data.template.id) {
        loadPreview(data.template.id);
      }
    } catch (err) {
      setError(t('templates.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (templateId) => {
    try {
      const data = await previewInvoiceNumber(templateId);
      setPreviewNumber(data.invoiceNumber);
    } catch (err) {
      console.error('Failed to load preview:', err);
    }
  };

  const updatePreview = () => {
    // Local preview calculation
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const number = String(1).padStart(formData.numberPadding || 4, '0');

    let preview = formData.numberFormat || '{PREFIX}-{YEAR}-{NUMBER}';
    preview = preview.replace('{PREFIX}', formData.numberPrefix || '');
    preview = preview.replace('{SUFFIX}', formData.numberSuffix || '');
    preview = preview.replace('{YEAR}', year);
    preview = preview.replace('{MONTH}', month);
    preview = preview.replace('{NUMBER}', number);
    preview = preview.replace(/--+/g, '-').replace(/^-+|-+$/g, '');

    setPreviewNumber(preview);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Handle reset counter mutual exclusivity
    if (name === 'resetCounterAnnually' && checked) {
      setFormData(prev => ({ ...prev, resetCounterMonthly: false }));
    } else if (name === 'resetCounterMonthly' && checked) {
      setFormData(prev => ({ ...prev, resetCounterAnnually: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditMode) {
        await updateInvoiceTemplate(id, formData);
      } else {
        await createInvoiceTemplate(formData);
      }
      navigate('/invoice-templates');
    } catch (err) {
      setError(err.response?.data?.error || t('templateForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">{t('templates.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">
{isEditMode ? t('templates.editTemplate') : t('templates.createTemplate')}
        </h1>
        <button
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          onClick={() => navigate('/invoice-templates')}
        >
          {t('common.cancel')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4">
            <h2 className="text-xl font-bold text-white">{t('templateForm.basicInformation')}</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templates.templateName')} *</label>
                <input
                  type="text"
                  name="name"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.type')} *</label>
                <select
                  name="type"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="outgoing">{t('templateForm.outgoingSales')}</option>
                  <option value="incoming">{t('templateForm.incomingPurchases')}</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.description')}</label>
              <textarea
                name="description"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.description}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleChange}
                  className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('templateForm.setAsDefault')}</span>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('users.active')}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Invoice Numbering */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4">
            <h2 className="text-xl font-bold text-white">{t('templateForm.invoiceNumbering')}</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
              <strong className="text-gray-700 dark:text-gray-300">{t('templateForm.preview')}:</strong>{' '}
              <code className="bg-white dark:bg-gray-800 px-3 py-1 rounded text-pastel-purple-600 dark:text-pastel-purple-400 font-semibold text-lg">
                {previewNumber || 'INV-2025-0001'}
              </code>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.numberPrefix')}</label>
                <input
                  type="text"
                  name="numberPrefix"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.numberPrefix}
                  onChange={handleChange}
                  maxLength="20"
                />
                <small className="text-xs text-gray-500 dark:text-gray-400">e.g., INV, INVOICE</small>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.numberSuffix')}</label>
                <input
                  type="text"
                  name="numberSuffix"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.numberSuffix}
                  onChange={handleChange}
                  maxLength="20"
                />
                <small className="text-xs text-gray-500 dark:text-gray-400">e.g., /2025</small>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templates.numberFormat')}</label>
              <input
                type="text"
                name="numberFormat"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.numberFormat}
                onChange={handleChange}
              />
              <small className="text-xs text-gray-500 dark:text-gray-400">
                {t('templateForm.availablePlaceholders')}: {'{PREFIX}'}, {'{SUFFIX}'}, {'{YEAR}'}, {'{MONTH}'}, {'{NUMBER}'}
              </small>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.numberPadding')}</label>
                <input
                  type="number"
                  name="numberPadding"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.numberPadding}
                  onChange={handleChange}
                  min="1"
                  max="10"
                />
                <small className="text-xs text-gray-500 dark:text-gray-400">{t('templateForm.numberPaddingHelp')}</small>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.resetCounter')}</label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="resetCounterAnnually"
                      checked={formData.resetCounterAnnually}
                      onChange={handleChange}
                      className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('templates.annually')}</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="resetCounterMonthly"
                      checked={formData.resetCounterMonthly}
                      onChange={handleChange}
                      className="w-4 h-4 text-pastel-purple-600 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('templates.monthly')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Defaults */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4">
            <h2 className="text-xl font-bold text-white">{t('templateForm.clientDefaults')}</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientName')}</label>
              <input
                type="text"
                name="clientName"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.clientName}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientEmail')}</label>
              <input
                type="email"
                name="clientEmail"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.clientEmail}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientAddress')}</label>
              <textarea
                name="clientAddress"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.clientAddress}
                onChange={handleChange}
                rows="3"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientNip')}</label>
              <input
                type="text"
                name="clientNip"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.clientNip}
                onChange={handleChange}
                placeholder="e.g., 123-456-78-90"
              />
            </div>
          </div>
        </div>

        {/* Invoice Defaults */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4">
            <h2 className="text-xl font-bold text-white">{t('templateForm.invoiceDefaults')}</h2>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.currency')}</label>
                <select
                  name="currency"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="PLN">PLN</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templates.taxRate')} (%)</label>
                <input
                  type="number"
                  name="taxRate"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.taxRate}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.paymentTermsDays')}</label>
                <input
                  type="number"
                  name="paymentTermsDays"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.paymentTermsDays}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.defaultDescription')}</label>
              <textarea
                name="defaultDescription"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.defaultDescription}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templateForm.defaultNotes')}</label>
              <textarea
                name="notes"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            onClick={() => navigate('/invoice-templates')}
          >
{t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 hover:from-pastel-purple-700 hover:to-pastel-pink-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
{loading ? t('common.loading') : isEditMode ? t('templateForm.updateTemplate') : t('templates.createTemplate')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceTemplateForm;
